import * as React from 'react';
import { trace } from 'owa-trace';
import {
    findScrollableParent,
    getFirstFocusable,
    getNextElement,
    getPreviousElement,
    isElementTabbable,
} from '@fluentui/react/lib/Utilities';

/**
 * Default manager for calendar surface items. Captures tab events and sorts the calendar items on the surface store
 * to determine which element to focus on next
 */
export class SurfaceFocusManager {
    private setFocusedElementCallback: (id: string) => void;
    private tabOrder: string[];
    private initialFocusId: string;
    private surfaceItemDOMElements: HTMLElement[];
    private removedItemIds: string[];

    // this is an internal copy of the currently focused model. This is not the same as store.focusedSurfaceItem, because when we tab out of the control,
    // store.focusedSurfaceItem = null (item lost focus), but we need to know which item to come back to when we re-enter control
    private currentlyFocusedId: string = null;
    private currentlyFocusedIndex: number = null;

    private isClickEvent: boolean = false;
    private isMouseDownOnNonItem: boolean = false;
    private isClearingIsClickedFlag: boolean = false;
    private isClearingMouseDownFlag: boolean = false;
    private RESET_FLAG_TIMEOUT: number = 200;

    /**
     * The key for the previous element to drop into the refs
     */
    private rootElementKey = 'rootElement';

    constructor(setFocusedElementCallback: (id: string) => void) {
        this.setFocusedElementCallback = setFocusedElementCallback;
        this.surfaceItemDOMElements = [];
    }

    public defineOrder(tabOrder: string[]) {
        // these should not be reset when redefining tab order
        let tempRootRef;
        if (this.surfaceItemDOMElements) {
            tempRootRef = this.surfaceItemDOMElements[this.rootElementKey];
        }

        this.surfaceItemDOMElements = [];

        let addedItemIds = [];
        let addedPreviouslyRemovedIds = [];
        if (this.tabOrder && tabOrder) {
            addedItemIds = tabOrder.filter(id => !this.tabOrder.includes(id));
            addedPreviouslyRemovedIds = this.removedItemIds.filter(id => addedItemIds.includes(id));
        }

        this.removedItemIds = [];
        if (this.tabOrder && tabOrder) {
            this.removedItemIds = this.tabOrder.filter(id => !tabOrder.includes(id));
        }

        // if items were deleted, we can move the focus
        if (this.removedItemIds.includes(this.currentlyFocusedId)) {
            setTimeout(this.resetFocusAfterDelete.bind(this), 0);
        }

        if (
            addedPreviouslyRemovedIds.length > 0 &&
            addedPreviouslyRemovedIds[0] == this.currentlyFocusedId
        ) {
            setTimeout(this.focusOnItem.bind(this), 0, addedPreviouslyRemovedIds[0], true);
        }

        this.tabOrder = tabOrder;
        this.registerRootElementReference(tempRootRef);
    }

    /**
     * Optionally sets an item that is getting focus on initial parent focus in.
     */
    public setInitialFocusId(id: string) {
        this.initialFocusId = id;
    }

    /**
     * Focuses on the item with the given tabId. Returns the reference to the item focused
     * @param id tabId of the item to focus
     * @param doNotScroll whether to focus on the item if it's outside the current view (causing a scroll)
     * @param focusIfCurrentlyFocusedItem whether to reset focus to the item if it's the most recently focused item
     * @returns The DOM element that focus was called on, or null if focus was not attempted
     */
    public focusOnItem(
        id: string,
        doNotScroll?: boolean,
        focusIfCurrentlyFocusedItem?: boolean
    ): HTMLElement {
        if (!id || !this.tabOrder.includes(id)) {
            // if we couldn't find the id, see if we can find anything like it
            let foundSubstitute = false;
            this.tabOrder.filter(tabId => {
                if (!foundSubstitute && tabId.indexOf(id) >= 0) {
                    trace.info(
                        `[SurfaceFocusManager] focusOnItem for id: ${id} failed, but found similar id: ${tabId}`
                    );
                    id = tabId;
                    foundSubstitute = true;
                }
            });
        }

        let shouldFocusElement = !(this.currentlyFocusedId == id && !focusIfCurrentlyFocusedItem);
        if (shouldFocusElement) {
            let focusedItem = this.surfaceItemDOMElements[id];
            return this.setFocusToElementByElementReference(focusedItem, doNotScroll);
        }
        return null;
    }

    /**
     * uses the cached currentlyfocusedId to set focus to the cached focused item. Returns the ref
     * to that item.
     */
    public focusOnFocusedElement(): HTMLElement {
        return this.focusOnItem(this.currentlyFocusedId, true, true);
    }

    /**
     * The action callback for when tabs occur
     */
    public onTab(evt: React.KeyboardEvent<HTMLDivElement>): void {
        let backwards = evt.shiftKey;
        let distance: number = backwards ? -1 : 1;

        let newIndex;
        let oldIndex;
        if (this.currentlyFocusedId) {
            oldIndex = this.tabOrder.indexOf(this.currentlyFocusedId);
            if (oldIndex < 0) {
                // currently focused item not found in array, was probably removed. So we default back to the beginning.
                newIndex = 0;
            } else {
                newIndex = oldIndex + distance;
            }
        } else {
            newIndex = backwards ? this.tabOrder.length - 1 : 0;
        }

        let newElementToFocus = this.findNextElementToFocus(
            newIndex,
            distance,
            this.endOfItemsReachedOnTab
        );
        if (newElementToFocus) {
            this.setFocusToElementByElementReference(newElementToFocus);
            evt.preventDefault();
        }
    }

    /**
     * The action callback for when the parent element gets any focus
     */
    public onFocus(evt: React.FocusEvent<HTMLElement>): void {
        evt.preventDefault();

        // handling the mouse down on a non-item case, needs to be ignored
        // in the onFocus callback, we're only trying to handle cases where the user is interacting with a
        // tabbable element like surface items or buttons. onFocus only fires with a non-tabbable element target when it's
        // trying to interact with the root, so we can ignore those
        if (this.isMouseDownOnNonItem || !isElementTabbable(evt.target as HTMLElement)) {
            return;
        }

        /**
         * When a focus event fires on the focus manager, there are several places it could have come from.
         * - Tabbing within the manager fires a focus event
         * - Focus coming in by tabbing forward or backward into the manager from another element
         * - clicking on anything inside of the manager
         * - using a skip link to jump to the manager
         *
         * Common cases are tabbing within the manager and clicking on an item in the manager.
         * If we're tabbing from within the manager, the evt.target for the focus should already be set to the
         * currentlyFocusedModelSurfaceElement, so we can just ignore this event.
         *
         * If we click on an element or tab into the surface for the first time, evt.target will be a new item, and we want
         * to set focus appropriately. We can check the evt.related target to see where we came from: if it was the previous node or next
         * node, this was a tab event so we know which item to focus on. Otherwise, we don't know where it came from, so we'll
         * try setting focus to the event target (this is the case for click events)
         */
        let currentlyFocusedModelSurfaceElement = this.currentlyFocusedId
            ? this.surfaceItemDOMElements[this.currentlyFocusedId]
            : null;
        let focusedDOMElement = getFirstFocusable(
            currentlyFocusedModelSurfaceElement,
            currentlyFocusedModelSurfaceElement,
            true /* includeElementsInFocusZones */
        );
        if (!this.currentlyFocusedId || !focusedDOMElement || focusedDOMElement !== evt.target) {
            let index;

            if (this.isClickEvent) {
                // see if we're trying to focus a specific item.
                // we might first be trying to focus an element in the surface. If so, we should set the current focus to the target and short circuit
                let shortCircuit = false;
                this.tabOrder.forEach((id, index) => {
                    let domElement = this.surfaceItemDOMElements[id];
                    if (domElement != null && domElement.contains(evt.target as HTMLElement)) {
                        this.currentlyFocusedId = id;
                        this.currentlyFocusedIndex = index;
                        this.setFocusedElementCallback(this.currentlyFocusedId);
                        shortCircuit = true;
                    }
                });

                this.delayedResetIsClickedFlag();

                if (shortCircuit) {
                    return;
                }

                // else, default to focusing on initial or first item
                index = this.getDefaultIndex();
            } else if (
                currentlyFocusedModelSurfaceElement &&
                currentlyFocusedModelSurfaceElement.contains(evt.target as Node)
            ) {
                // if this isn't a click event, and the focus is being called on the already-focused item, do nothing
                return;
            } else {
                // default to initial or first item
                index = this.getDefaultIndex();
            }

            let element = this.findNextElementToFocus(index, 1, null);
            this.setFocusToElementByElementReference(element, false);
        } else {
            this.setFocusedElementCallback(this.currentlyFocusedId);
            this.delayedResetIsClickedFlag();
        }
    }

    private getDefaultIndex(): number {
        return this.initialFocusId ? Math.max(0, this.tabOrder.indexOf(this.initialFocusId)) : 0;
    }

    public onMouseDown(evt: React.MouseEvent<HTMLDivElement>) {
        // in IE, mouseDown by default tries to call .focus() on the next focusable item in the DOM (rather than waiting like other browsers do).
        // this fires an onFocus(evt) that looks exactly like a tab event. Here we check if a mousedown is occurring that doesn't actually try to
        // focus on a specific item (which would be handled correctly already), and sets a flag so that we can ignore that focus event
        let mouseDownNotOnItem = true;
        this.tabOrder.forEach((id, index) => {
            let domElement = this.surfaceItemDOMElements[id];
            if (domElement && domElement.contains(evt.target as HTMLElement)) {
                mouseDownNotOnItem = false;
            }
        });
        if (mouseDownNotOnItem) {
            // we had a mouseDown somewhere on the surface but not on any item
            this.isMouseDownOnNonItem = true;
            this.delayedResetMouseDownFlag();
        }
    }

    /**
     * The focusOut callback for when we leave the managed component
     * @param evt
     */
    public onFocusOut(evt: React.FocusEvent<HTMLElement>): void {
        this.setFocusedElementCallback(null);
    }

    /**
     * Registers the 'root' dom element for the control
     */
    public registerRootElementReference(rootElement: HTMLElement): void {
        this.surfaceItemDOMElements[this.rootElementKey] = rootElement;
    }

    /**
     * Registers an element to manage
     */
    public registerTabbableElement(element: HTMLElement, key: string): void {
        let registeredElement = this.surfaceItemDOMElements[key];
        let rootElement = this.surfaceItemDOMElements[this.rootElementKey];
        if (!(registeredElement && rootElement && rootElement.contains(registeredElement))) {
            this.surfaceItemDOMElements[key] = element;
        }
    }

    /**
     * Registers bound event handlers
     */
    public registerEventHandlers(
        handlers: React.DOMAttributes<HTMLDivElement>
    ): React.DOMAttributes<HTMLDivElement> {
        let boundEventHandlers = {};
        Object.keys(handlers).forEach(key => {
            boundEventHandlers[key] = handlers[key].bind(this);
        });

        return boundEventHandlers;
    }

    /**
     * The handler for when items are clicked, to update the focused item
     * @param item the clicked item
     */
    public itemClicked(element: HTMLElement): void {
        this.isClickEvent = true;
        let surfaceItemTabOrderId = element.getAttribute('data-tabid');

        if (this.tabOrder.indexOf(surfaceItemTabOrderId) < 0) {
            // if the item isn't in the tab order, it's part of a multi-week snake
            // build the ID of the clicked item the same way it's built for multi-week snakes in month view
            // item.id isn't unique enough on it's own so we use itemindex data attribute also
            if (element.dataset.itemindex) {
                surfaceItemTabOrderId = surfaceItemTabOrderId + '_' + element.dataset.itemindex;
            }
        }

        this.currentlyFocusedId = surfaceItemTabOrderId;
        this.currentlyFocusedIndex = this.tabOrder.indexOf(surfaceItemTabOrderId);
        this.setFocusedElementCallback(surfaceItemTabOrderId);
    }

    /**
     * Takes a desired index and whether to search forwards or backwards, and searches for the next valid element in that direction
     * starting from the desired index. Fires the given callback if the end of the surfaceItemDOMElements is reached in the desired direction
     * and no valid elements are found. Does not set currentlyFocusedId or currentlyFocusedIndex
     * @param nextDesiredIndex the first index to check for valid focusable elements. If it's an invalid index or there are no focusable elements, will search for
     * another index
     * @param direction 1 for going forwards, -1 for going backwards
     * @param onEndOfItemsReached callback function for what to do if the function can't find any valid index in the given direction at or after the initial nextDesiredIndex
     * @returns the DOM reference to the next item in the tab order to focus. This is the parent DOM from surfaceItemDOMElements, and is not necessarily the first focusable child
     */
    private findNextElementToFocus(
        nextDesiredIndex: number,
        direction: number,
        onEndOfItemsReached: (oldIndex: number, direction: number) => HTMLElement
    ): HTMLElement {
        let firstFocusable: HTMLElement = null;
        let surfaceItem: HTMLElement = null;
        let oldIndex = nextDesiredIndex - direction;
        /**
         * Not all items in the surface element list are necessarily focusable. We'll loop to keep searching for the "next" focusable item until we actually
         * find one
         */
        while (firstFocusable == null) {
            let newItemId = this.tabOrder[nextDesiredIndex];

            // when we're at the beginning going backwards, or at the end going forwards, we have to override the default tabbing because all-day
            // items all come before daily items.
            if (nextDesiredIndex >= this.tabOrder.length) {
                return onEndOfItemsReached ? onEndOfItemsReached(oldIndex, direction) : null;
            } else if (nextDesiredIndex < 0) {
                return onEndOfItemsReached ? onEndOfItemsReached(oldIndex, direction) : null;
            } else {
                surfaceItem = this.surfaceItemDOMElements[newItemId];
            }

            firstFocusable = getFirstFocusable(
                surfaceItem,
                surfaceItem,
                true /* includeElementsInFocusZones */
            );

            // in case a consumer has items on the surface that are wrapped to gridItems but do not have tab stops in them, we need to find the next
            // item and try again.
            nextDesiredIndex = nextDesiredIndex + direction;
        }

        return surfaceItem;
    }

    /**
     * Given an element reference and whether or not to scroll, finds the focusable child inside the given element reference
     * and tries to set focus to it. Grabs the tabId from the element reference and updates currentlyFocusedId and currentlyFocusedIndex.
     * @param elementReference the element to set the reference to
     * @param doNotScroll if true, and the item is offscreen, prevents setting focus
     * @returns The DOM element that was focused. If no focus was attempted, returns null
     */
    private setFocusToElementByElementReference(
        elementReference: HTMLElement,
        doNotScroll?: boolean
    ): HTMLElement {
        if (!elementReference) {
            return null;
        }

        let id = elementReference.getAttribute('data-tabid');
        if (!id) {
            // check to see if any of the items in surfacefocuselements contain the given ref
            for (let i = 0; i < this.tabOrder.length; i++) {
                let item = this.surfaceItemDOMElements[this.tabOrder[i]];
                if (item && item.contains(elementReference)) {
                    id = item.getAttribute('data-tabid');
                }
            }
        }

        let focusElement = getFirstFocusable(
            elementReference,
            elementReference,
            true /*includeElementsInFocusZones*/
        );
        let scrollParent = this.findScrollParent(focusElement);

        let isOutsideViewport =
            scrollParent &&
            (elementReference.offsetTop < scrollParent.scrollTop ||
                elementReference.offsetTop > scrollParent.scrollTop + scrollParent.offsetHeight);

        if (focusElement && !(doNotScroll && isOutsideViewport)) {
            this.currentlyFocusedId = id;
            this.currentlyFocusedIndex = this.tabOrder.indexOf(id);
            focusElement.focus();
            if (isOutsideViewport) {
                // calling itemEl.scrollIntoView() here breaks IE because IE11 scrolls the entire page horizontally
                // see https://social.technet.microsoft.com/Forums/ie/en-US/b8458796-8360-4512-b88a-62190f421ef7/issues-with-scrollintoview-javascript-method-in-ie10?forum=ieitprocurrentver

                scrollParent.scrollTop = this.findScrollOffset(focusElement, scrollParent);
            }
            this.setFocusedElementCallback(id);
            return focusElement;
        }
        return null;
    }

    /**
     * Finds the scrollable parent for the given element. Different browsers give different offsets for various borders, paddings, etc. so we use a
     * buffer to ignore small paddings to find the scroll parent most likely to be the real scroll well.
     * @param element
     */
    private findScrollParent(element: HTMLElement): HTMLElement {
        // first use the shortcut fabric function to see if there's a parent since that's fast
        let parent = findScrollableParent(element);
        if (parent != document.body && parent != (window as any)) {
            return parent as HTMLElement;
        }

        // if there's no property data-is-scrollable in the DOM above this, then we have to parse the parent ourselves
        // Edge sometimes has non-scrolling elements have a scrollHeight that's not exactly equal to the offsetHeight,
        // so we can add a small buffer to make sure we're finding the actual scroll well if there is one
        let buffer = 4;
        if (
            !element ||
            !element.offsetParent ||
            element.offsetParent.scrollHeight >
                (element.offsetParent as HTMLElement).offsetHeight + buffer
        ) {
            return element;
        }

        while (
            element &&
            element != this.surfaceItemDOMElements[this.rootElementKey] &&
            element.scrollHeight <= element.offsetHeight + buffer
        ) {
            element = element.parentElement as HTMLElement;
        }
        return element;
    }

    /**
     * If a parent has a scrollHeight > offsetHeight, even if there is no scroll functionality on it, then the child's call to
     * 'offsetTop' will be the offset top from that parent, and not from the actual scroll well we found. So we can iterively go
     * up the parent stack until we find the real parent and add all the offset tops together to find the real scroll offset relative
     * to the parent
     */
    private findScrollOffset(element: HTMLElement, parent: HTMLElement): number {
        let top = 0;
        do {
            top += element.offsetTop - element.scrollTop;
            element = element.offsetParent as HTMLElement;
        } while (element != parent && !!element);

        // adjust the offset to center the item in the parent rather than putting it at the top, like the default behavior of element.focus()
        top -= parent.clientHeight / 2;

        return top;
    }

    private onFocusOutOfLastElement = (
        oldTabIndex: number,
        oldModel: HTMLElement,
        removedListener
    ) => () => {
        // reset the state
        let firstFocusable = getFirstFocusable(
            oldModel,
            oldModel,
            true /* includeElementsInFocusZones */
        );
        firstFocusable.removeEventListener('focusOut', removedListener);
        firstFocusable.tabIndex = oldTabIndex;
        this.setFocusedElementCallback(null);
    };

    private resetFocusAfterDelete() {
        if (this.removedItemIds.length > 0) {
            let newIndex;
            let oldIndex;

            // only change the focus if the item we're focused on was the one removed
            if (this.removedItemIds[0] == this.currentlyFocusedId) {
                oldIndex = this.tabOrder.indexOf(this.removedItemIds[0]);
                if (oldIndex < 0) {
                    // item not found in array try to use the old index
                    newIndex = this.currentlyFocusedIndex;
                } else {
                    // item still exists, so leave focus on it
                    newIndex = oldIndex;
                }
            } else {
                return;
            }

            let newElementToFocus = this.findNextElementToFocus(
                newIndex,
                1,
                this.endOfItemsReachedAfterDelete
            );

            this.setFocusToElementByElementReference(newElementToFocus, false);
        }
    }

    private endOfItemsReachedAfterDelete = (oldIndex: number, direction: number) => {
        // direction always forward after delete
        // if we couldn't find the next item, try instead to go backwards
        return this.findNextElementToFocus(oldIndex, -1, this.endOfFocusedItemsReachedReverse);
    };

    private endOfItemsReachedOnTab = (oldIndex: number, direction: number) => {
        if (direction > 0) {
            return this.endOfFocusedItemsReachedForward(oldIndex);
        } else {
            return this.endOfFocusedItemsReachedReverse();
        }
    };

    private endOfFocusedItemsReachedForward = (oldIndex: number): HTMLElement => {
        // first we check whether there are any other tabbable elements going forward
        let root = this.surfaceItemDOMElements[this.rootElementKey];
        let nextElement = getNextElement(
            document.body,
            root,
            true /* checkNode */,
            false /* suppressParentTraversal*/,
            true /* suppressChildTraversal */,
            true /* include items in focus zones */,
            false /* allowFocusRoot */
        );

        if (nextElement) {
            return nextElement;
        }

        /**
         * This is a difficult case. If we're tabbing out of the last element in the component (newIndex > last index), but nextElement doesn't exist,
         * we don't know for sure where focus should go (this case occurs if the managed component is the last item in the dom)
         * . We can't necessarily rely on the default TAB behavior here because of the case where:
         *
         * | Friday | Saturday    |
         * ---------|-------------
         *          | AllDayEvent
         * ---------|-------------
         * 12pm evt |
         * ---------|-------------
         *
         * in this example, the "last" item by time is the all day event, but the last item in the DOM is the 12pm event. If we let default tabbing
         * handle this, tabbing from all day event will go to the first non-all day item, rather than whatever comes next.
         *
         * Here, we'll get around that by using a tabIndex browser hack. Setting tabIndex > 0 changes the default flow of the DOM, so if we set a
         * tabIndex on the last item before resolving the tab, the focus will go back to root, which we expect if we're on the last tabbable element.
         *
         * Setting this specific tab index isn't ideal though, so we need to clean up afterward. We do this by maintaining a copy of the tabIndex of the
         * element we changed, then setting a callback function for "onFocusOut" event.
         *
         * The tab resolves with tabIndex > 0, then the onFocusOut event fires into our callback. In the callback, since the tab has already resolved, we
         * can now remove the 'focusout' listener and change the tab index back to oldTabIndex.
         **/

        let oldItemId = this.tabOrder[oldIndex];
        let element = this.surfaceItemDOMElements[oldItemId];
        let firstFocusable = getFirstFocusable(
            element,
            element,
            true /* includeElementsInFocusZones */
        );
        let oldTabIndex = firstFocusable.tabIndex;

        // by using a specific tab index, we jump over other calendar surface items
        firstFocusable.tabIndex = 2;
        firstFocusable.addEventListener(
            'focusout',
            this.onFocusOutOfLastElement(oldTabIndex, firstFocusable, this.onFocusOut)
        );
        this.currentlyFocusedId = oldItemId;
        this.currentlyFocusedIndex = oldIndex;
        return null;
    };

    private endOfFocusedItemsReachedReverse = () => {
        // there's no previous key defined, try to find the "previous" element in DOM order using focus utilities
        let root = this.surfaceItemDOMElements[this.rootElementKey];
        return getPreviousElement(
            document.body,
            root,
            true /* checkNode */,
            false /* suppressParentTraversal*/,
            false /* traverseChildren */,
            true /* include items in focus zones */,
            false /* allowFocusRoot */,
            true /* must be tabbable */
        );
    };

    private delayedResetMouseDownFlag = () => {
        if (!this.isClearingMouseDownFlag) {
            this.isClearingMouseDownFlag = true;
            setTimeout(this.resetMouseDownFlag, this.RESET_FLAG_TIMEOUT);
        }
    };

    private resetMouseDownFlag = () => {
        this.isMouseDownOnNonItem = false;
        this.isClearingMouseDownFlag = false;
    };

    private delayedResetIsClickedFlag = () => {
        if (!this.isClearingIsClickedFlag) {
            this.isClearingIsClickedFlag = true;
            setTimeout(this.resetIsClickedFlag, this.RESET_FLAG_TIMEOUT);
        }
    };

    private resetIsClickedFlag = () => {
        this.isClickEvent = false;
        this.isClearingIsClickedFlag = false;
    };
}
